import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { CLEANER_LOADERS } from './utils';
import { CleanersState } from './model';
import _keyBy from 'lodash/keyBy';
import dayjs from 'dayjs';
import { selectNewBooking } from '../bookings/selectors';
import { generateTimeSlotsBadges } from '../map/utils';

export const selectCleanersState = (state: RootState) => state.cleaners;

export const selectCleaners = createSelector(
  selectCleanersState,
  ({ cleaners }: CleanersState) => cleaners,
);

export const selectLoaders = createSelector(
  selectCleanersState,
  ({ loaders }: CleanersState) => loaders,
);

export const selectFavorites = createSelector(
  selectCleanersState,
  ({ favorites }: CleanersState) => favorites,
);

export const selectFavoritesByCleanerId = createSelector(
  selectFavorites,
  (favorites) => _keyBy(favorites, 'cleanerId'),
);

export const makeSelectFavoriteById = (id: string) =>
  createSelector(selectFavoritesByCleanerId, (favorites) => favorites?.[id]);

export const selectNewCleaner = createSelector(
  selectCleanersState,
  ({ newCleaner }: CleanersState) => newCleaner ?? {},
);

export const makeSelectCleanersLoader = (key: CLEANER_LOADERS) =>
  createSelector(selectCleanersState, ({ loaders }) => loaders[key] ?? false);

export const selectCurrentCleaner = createSelector(
  selectCleanersState,
  ({ currentCleaner }: CleanersState) => currentCleaner,
);

export const selectAvailableSlots = createSelector(
  selectCurrentCleaner,
  selectNewBooking,
  (cleaner, newBooking) => {
    if (newBooking?.requestDate) {
      const dayNumber = dayjs.unix(newBooking.requestDate).day();
      const currentSlots = cleaner.slots?.[dayNumber] ?? [];
      return generateTimeSlotsBadges(
        currentSlots,
        newBooking.slots,
        newBooking.requestDate,
      );
    }
    return [];
  },
);

export const selectCleanersPagination = createSelector(
  selectCleanersState,
  ({ pagination }) => pagination,
);

export const selectIsEditingCleaner = createSelector(
  selectCleanersState,
  ({ editingCleaner }) => !!editingCleaner,
);

export const selectEditingCleaner = createSelector(
  selectCleanersState,
  ({ editingCleaner }) => editingCleaner,
);

export const selectCleanerProfile = createSelector(
  selectCleanersState,
  ({ cleanerProfile }) => {
    if (cleanerProfile) {
      const totalReviews = cleanerProfile.reviews.length;

      const rating = cleanerProfile.reviews.length
        ? Number(
            (
              cleanerProfile.reviews.reduce(
                (acc, { rating }) => acc + rating,
                0,
              ) / totalReviews
            ).toFixed(2),
          )
        : 0;
      return {
        ...cleanerProfile,
        totalReviews,
        rating,
      };
    }
    return null;
  },
);
